import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'

const PaymentMethodMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    paymentMethodCode: '',
    paymentMethodName: '',
    paymentMethodDescription: '',
    status: 'Active',
    paymentType: 'Other',
    requiresProcessing: false,
    processingFee: 0,
    processingFeeType: 'Fixed',
    settlementDays: 0,
    accountDetails: {
      accountNumber: '',
      accountName: '',
      bankName: '',
      ifscCode: ''
    },
    minAmount: 0,
    maxAmount: 0,
    sortOrder: 0,
    icon: 'credit-card',
    color: '#222448',
    notes: ''
  })
  const [isLoadingPaymentMethodCode, setIsLoadingPaymentMethodCode] = useState(true)

  // Fetch next payment method code
  useEffect(() => {
    const fetchPaymentMethodCode = async () => {
      try {
        setIsLoadingPaymentMethodCode(true)
        const response = await paymentMethodMasterService.getNextPaymentMethodCode()
        if (response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            paymentMethodCode: response.data.paymentMethodCode || response.data.formattedValue || 'PMTH0001'
          }))
        }
      } catch (error) {
        console.error('Error fetching payment method code:', error)
        setFormData(prev => ({ ...prev, paymentMethodCode: 'PMTH0001' }))
      } finally {
        setIsLoadingPaymentMethodCode(false)
      }
    }
    fetchPaymentMethodCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name.startsWith('accountDetails.')) {
      const accountField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        accountDetails: {
          ...prev.accountDetails,
          [accountField]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.paymentMethodName) {
        toast.error('Please fill in all required fields')
        return
      }

      const response = await paymentMethodMasterService.createPaymentMethod(formData)
      if (response.success) {
        toast.success('Payment method created successfully!')
        navigate('/expenses/payment-method-master')
      } else {
        toast.error(response.message || 'Failed to create payment method')
      }
    } catch (error) {
      console.error('Error saving payment method:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        toast.error('Failed to create payment method')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      paymentMethodCode: formData.paymentMethodCode, // Keep the code
      paymentMethodName: '',
      paymentMethodDescription: '',
      status: 'Active',
      paymentType: 'Other',
      requiresProcessing: false,
      processingFee: 0,
      processingFeeType: 'Fixed',
      settlementDays: 0,
      accountDetails: {
        accountNumber: '',
        accountName: '',
        bankName: '',
        ifscCode: ''
      },
      minAmount: 0,
      maxAmount: 0,
      sortOrder: 0,
      icon: 'credit-card',
      color: '#222448',
      notes: ''
    })
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Payment Method Master', path: '/expenses/payment-method-master', onClick: () => navigate('/expenses/payment-method-master') },
    { label: 'New Payment Method', path: '/expenses/payment-method-master/form', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Payment Method Master</h1>
          <p>Create new payment method</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/expenses/payment-method-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Payment Method Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Payment Method Details</h3>
          </div>

          {/* Row 1: Payment Method Code, Name, Status, Type */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentMethodCode" className="form-label required">
                Payment Method Code
              </label>
              <input
                type="text"
                id="paymentMethodCode"
                name="paymentMethodCode"
                value={isLoadingPaymentMethodCode ? 'Generating...' : formData.paymentMethodCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentMethodName" className="form-label required">
                Payment Method Name
              </label>
              <input
                type="text"
                id="paymentMethodName"
                name="paymentMethodName"
                value={formData.paymentMethodName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter payment method name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentType" className="form-label">
                Payment Type
              </label>
              <select
                id="paymentType"
                name="paymentType"
                value={formData.paymentType}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Cheque">Cheque</option>
                <option value="Digital Wallet">Digital Wallet</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Row 2: Description */}
          <div className="form-group">
            <label htmlFor="paymentMethodDescription" className="form-label">
              Description
            </label>
            <textarea
              id="paymentMethodDescription"
              name="paymentMethodDescription"
              value={formData.paymentMethodDescription}
              onChange={handleInputChange}
              className="form-input"
              rows="2"
              placeholder="Enter payment method description"
            />
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Processing</h3>
            </div>
          </div>

          {/* Row 3: Processing Details */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="requiresProcessing" className="form-label">
                <input
                  type="checkbox"
                  id="requiresProcessing"
                  name="requiresProcessing"
                  checked={formData.requiresProcessing}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Requires Processing
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="processingFee" className="form-label">
                Processing Fee
              </label>
              <input
                type="number"
                id="processingFee"
                name="processingFee"
                value={formData.processingFee}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
                disabled={!formData.requiresProcessing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="processingFeeType" className="form-label">
                Fee Type
              </label>
              <select
                id="processingFeeType"
                name="processingFeeType"
                value={formData.processingFeeType}
                onChange={handleInputChange}
                className="form-input"
                disabled={!formData.requiresProcessing}
              >
                <option value="Fixed">Fixed</option>
                <option value="Percentage">Percentage</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="settlementDays" className="form-label">
                Settlement Days
              </label>
              <input
                type="number"
                id="settlementDays"
                name="settlementDays"
                value={formData.settlementDays}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Amount Limits</h3>
            </div>
          </div>

          {/* Row 4: Amount Limits */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="minAmount" className="form-label">
                Minimum Amount
              </label>
              <input
                type="number"
                id="minAmount"
                name="minAmount"
                value={formData.minAmount}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxAmount" className="form-label">
                Maximum Amount
              </label>
              <input
                type="number"
                id="maxAmount"
                name="maxAmount"
                value={formData.maxAmount}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sortOrder" className="form-label">
                Sort Order
              </label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="color" className="form-label">
                Color
              </label>
              <input
                type="color"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="form-input"
                style={{ height: '38px' }}
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Account Details (Optional)</h3>
            </div>
          </div>

          {/* Account Details Section */}

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="accountDetails.accountNumber" className="form-label">
                Account Number
              </label>
              <input
                type="text"
                id="accountDetails.accountNumber"
                name="accountDetails.accountNumber"
                value={formData.accountDetails.accountNumber}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter account number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountDetails.accountName" className="form-label">
                Account Name
              </label>
              <input
                type="text"
                id="accountDetails.accountName"
                name="accountDetails.accountName"
                value={formData.accountDetails.accountName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter account name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountDetails.bankName" className="form-label">
                Bank Name
              </label>
              <input
                type="text"
                id="accountDetails.bankName"
                name="accountDetails.bankName"
                value={formData.accountDetails.bankName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter bank name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountDetails.ifscCode" className="form-label">
                IFSC Code
              </label>
              <input
                type="text"
                id="accountDetails.ifscCode"
                name="accountDetails.ifscCode"
                value={formData.accountDetails.ifscCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter IFSC code"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PaymentMethodMasterForm

